/* campclub.js — single source of truth; normalize to exactly one membership */
document.addEventListener('DOMContentLoaded', () => {
  // Prevent double-binding if the asset is included twice or the DOM re-inits
  if (window.__CC_CAMPCLUB_BOUND__) return;
  window.__CC_CAMPCLUB_BOUND__ = true;

  const offer = document.querySelector('#campclub-offer, .campclub-offer');
  if (!offer) return;

  // Accept either id or class for the checkbox
  const cb  = offer.querySelector('#campclub-add, .cc-add-membership');
  const msg = document.getElementById('campclub-msg');

  // Prefer data-* ids from markup; fallback to JSON only if missing
  const HANDLE  = (offer.getAttribute('data-campclub-product-handle') || '').trim();
  const VAR_ID  = (offer.getAttribute('data-campclub-variant-id') || '').trim();
  const PLAN_ID = (offer.getAttribute('data-campclub-selling-plan-id') || '').trim();

  const showMsg = (t) => { if (msg) { msg.style.display = 'block'; msg.textContent = t; } };
  const hideMsg = () => { if (msg) { setTimeout(() => (msg.style.display = 'none'), 1200); } };

  async function getIds() {
    if (VAR_ID && PLAN_ID) return { variantId: VAR_ID, planId: PLAN_ID };
    if (!HANDLE) throw new Error('Missing membership handle and data ids');
    const res = await fetch(`/products/${HANDLE}.js`, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(`Product JSON fetch failed (${res.status})`);
    const p = await res.json();
    const variantId = String(p?.variants?.[0]?.id || '').trim();
    const planId = String(p?.selling_plan_groups?.[0]?.selling_plans?.[0]?.id || '').trim();
    if (!variantId) throw new Error('No variant id (check visibility/variants)');
    if (!planId) throw new Error('No selling plan id (attach plan & save)');
    return { variantId, planId };
  }

  const getCart = () =>
    fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then((r) => r.json());

  // Return ALL membership lines (handles both locations Shopify can store plan id)
  function membershipLines(cart, vid, pid) {
    const lines = [];
    (cart.items || []).forEach((it, i) => {
      const plan =
        it.selling_plan_id ||
        (it.selling_plan_allocation && it.selling_plan_allocation.selling_plan_id) ||
        null;
      if (String(it.variant_id) === String(vid) && String(plan) === String(pid)) {
        lines.push({ index: i + 1, quantity: it.quantity || 1 });
      }
    });
    return lines;
  }

  async function addMembership(vid, pid) {
    const r = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ items: [{ id: Number(vid), quantity: 1, selling_plan: Number(pid) }] })
    });
    if (!r.ok) throw new Error((await r.text()) || `HTTP ${r.status}`);
  }

  async function removeLineByIndex(lineIndex) {
    const r = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ line: lineIndex, quantity: 0 })
    });
    if (!r.ok) throw new Error((await r.text()) || `HTTP ${r.status}`);
  }

  async function setLineQty(lineIndex, qty) {
    const r = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ line: lineIndex, quantity: qty })
    });
    if (!r.ok) throw new Error((await r.text()) || `HTTP ${r.status}`);
  }

  // 🔒 Normalizer: after any add/remove, force EXACTLY ONE membership (qty = 1)
  async function normalizeToOne(vid, pid) {
    const cart = await getCart();
    const lines = membershipLines(cart, vid, pid);
    if (!lines.length) return; // nothing to normalize

    // Keep the first line at qty 1
    if (lines[0].quantity !== 1) await setLineQty(lines[0].index, 1);

    // Remove all other lines (if any)
    for (let i = 1; i < lines.length; i++) {
      await removeLineByIndex(lines[i].index);
    }
  }

  // Initialize the checkbox based on cart state
  (async () => {
    try {
      const { variantId, planId } = await getIds();
      const cart = await getCart();
      const lines = membershipLines(cart, variantId, planId);
      if (cb) cb.checked = lines.length > 0;
    } catch (e) {
      showMsg(e?.message || 'Init failed');
      console.error('[CampClub init]', e);
    }
  })();

  // Handle toggles (with a guard so repeat events don’t collide)
  let busy = false;
  cb &&
    cb.addEventListener('change', async () => {
      if (busy) return;
      busy = true;
      try {
        const { variantId, planId } = await getIds();
        const cart = await getCart();
        const lines = membershipLines(cart, variantId, planId);

        if (cb.checked) {
          showMsg('Adding membership…');
          // Only add if not already present
          if (!lines.length) await addMembership(variantId, planId);
          // Normalize in case another handler also added one
          await normalizeToOne(variantId, planId);
        } else {
          showMsg('Removing membership…');
          // Remove all membership lines if present
          for (const l of lines) await removeLineByIndex(l.index);
        }

        // Ask the theme to refresh its cart UI if it listens for this event
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      } catch (e) {
        let text = 'Could not update membership. ';
        try {
          text += JSON.parse(e.message)?.description || e.message;
        } catch {
          text += e.message || 'Please try again.';
        }
        showMsg(text);
        console.error('[CampClub]', e);
      } finally {
        busy = false;
        hideMsg();
      }
    });
});
